import { Injectable } from '@angular/core';
import { BcRestApi } from 'common/api/rest-api';
import { from, ReplaySubject } from 'rxjs';
import { PCacheable } from 'ngx-cacheable';
import { AgentStat, AgentStatIndicator, Source } from '../models/agent-stat.model';
import { SourceTypes } from '../agent-stat.config';
import * as moment from 'moment';
import * as debouncePromise from 'debounce-promise';

export const AGENT_STAT_DEBOUNCE_TIME = 200;
export const TRIGGER_DATA_REFRESH_TIMEOUT = 10000;

@Injectable()
export class StatSourceService {

  constructor(private _bcRestApi: BcRestApi) {
  }

  @PCacheable({
    maxCacheCount: 10
  })
  getAgentStatObservable(sourceType: SourceTypes, indicator: AgentStatIndicator): Promise<Source> {
    const subject = new ReplaySubject<AgentStat>(1);
    const obs = from(subject);

    const sourceObject = {
      obs,
      refresh: () => {
        setTimeout(() => {
          this._triggerDataRefresh(sourceType, indicator, subject);
        }, TRIGGER_DATA_REFRESH_TIMEOUT);
      }
    };
    // Preload initial data for source
    this._triggerDataRefresh(sourceType, indicator, subject);
    return Promise.resolve(sourceObject);
  }

  public getSource(sourceType, indicator): Promise<Source> {
    return this.getAgentStatObservable(sourceType, indicator);
  }

  private _triggerDataRefresh(sourceType: SourceTypes, indicator: AgentStatIndicator, subject: ReplaySubject<AgentStat>): Promise<void> {
    return this._getAgentStatObservable_internal(sourceType, indicator).then(result => {
      return subject.next(result[indicator.agentStatSettingID]);
    });
  }

  private _getAgentStatObservable_internal(sourceType, indicator): Promise<Record<number, AgentStat>> {
    switch (sourceType) {
      case SourceTypes.GET_CHAT_CLOSED_COUNT_OBSERVABLE:
      case SourceTypes.CONVERSION_AMOUNT_OBSERVABLE:
      case SourceTypes.CONVERSION_COUNT_OBSERVABLE:
      case SourceTypes.SURVEY_NPS_OBSERVABLE:
      case SourceTypes.SURVEY_KNOWLEDGE_OBSERVABLE:
      case SourceTypes.SURVEY_PROFESSIONALISM_OBSERVABLE:
      case SourceTypes.SURVEY_OVERALL_OBSERVABLE:
      case SourceTypes.SURVEY_RESPONSIVENESS_OBSERVABLE:
        return this._debouncedGetAgentStats(indicator);
      default:
        break;
    }
  }

  private _debouncedGetAgentStats = debouncePromise((argsList) => {
    const timeZoneOffset = moment().utcOffset();
    const dayOfWeek = moment().weekday();
    const indicators = argsList.reduce((set, args) => set.add(args[0]), new Set<string>());
    const agentStatPromise = this._bcRestApi.getAgentStats([...indicators.values()], timeZoneOffset, dayOfWeek);
    return argsList.map(() => agentStatPromise);
  }, AGENT_STAT_DEBOUNCE_TIME, { accumulate: true });
}
